import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import useCheckDnaSuggestions from "@/api/dna/useCheckDnaSuggestions";
import { Refinement } from "$/models/Refinement";
import useRejectDnaRefinement from "@/api/dna/useRejectDnaRefinement.ts";
import useApplyDnaRefinement from "@/api/dna/useApplyDnaRefinement.ts";
import DnaChangeSuggestionModal from "@/components/modals/DnaChangeSuggestionModal/DnaChangeSuggestionModal.tsx";

interface DnaRefinementContextProps {
  currentRefinement: Refinement | null;
  handleGetRefinement: (tripId?: string) => Promise<void>;
  errorCheckForSuggestions: Error | null;
  handleRejectRefinement: () => Promise<void>;
  errorDeleteRefinement: Error | null;
  handleApplyRefinement: () => Promise<void>;
  errorApplyRefinement: Error | null;
  isLoadingAction: boolean;
}

const DnaRefinementContext = createContext<DnaRefinementContextProps>(
  undefined!,
);

export const DnaRefinementProvider = ({ children }: PropsWithChildren) => {
  const [currentRefinement, setCurrentRefinement] = useState<Refinement | null>(
    null,
  );
  const hasCheckedForPendingRefinement = useRef(false);

  const {
    mutateAsync: checkForSuggestions,
    isPending: isLoadingCheckForSuggestions,
    error: errorCheckForSuggestions,
  } = useCheckDnaSuggestions();

  const {
    mutateAsync: applyRefinement,
    isPending: isLoadingApplyRefinement,
    error: errorApplyRefinement,
  } = useApplyDnaRefinement();

  const {
    mutateAsync: rejectRefinement,
    isPending: isLoadingDeleteRefinement,
    error: errorDeleteRefinement,
  } = useRejectDnaRefinement();

  const isLoadingAction = isLoadingCheckForSuggestions ||
    isLoadingApplyRefinement || isLoadingDeleteRefinement;

  const handleGetRefinement = useCallback(
    async (_tripId: string | undefined = undefined) => {
      try {
        if (isLoadingCheckForSuggestions) return;

        const { data } = await checkForSuggestions();

        setCurrentRefinement(data);
      } catch (error) {
        console.error("Error checking for DNA suggestions:", error);
      }
    },
    [
      checkForSuggestions,
      setCurrentRefinement,
      isLoadingCheckForSuggestions,
    ],
  );

  useEffect(() => {
    if (hasCheckedForPendingRefinement.current) return;

    hasCheckedForPendingRefinement.current = true;
    void handleGetRefinement();
  }, [handleGetRefinement]);

  const handleApplyRefinement = useCallback(async () => {
    try {
      if (isLoadingApplyRefinement || !currentRefinement) return;

      await applyRefinement(currentRefinement.id);

      setCurrentRefinement(null);
    } catch (error) {
      console.error("Error applying DNA suggestions:", error);
    }
  }, [
    applyRefinement,
    setCurrentRefinement,
    isLoadingApplyRefinement,
    currentRefinement,
  ]);

  const handleRejectRefinement = useCallback(async () => {
    try {
      if (isLoadingDeleteRefinement || !currentRefinement) return;

      await rejectRefinement(currentRefinement.id);

      setCurrentRefinement(null);
    } catch (error) {
      console.error("Error rejecting refinement:", error);
    }
  }, [currentRefinement, rejectRefinement, isLoadingDeleteRefinement]);

  return (
    <DnaRefinementContext.Provider
      value={{
        currentRefinement,
        handleGetRefinement,
        errorCheckForSuggestions,
        handleRejectRefinement,
        errorDeleteRefinement,
        handleApplyRefinement,
        errorApplyRefinement,
        isLoadingAction,
      }}
    >
      {children}
      {currentRefinement
        ? <DnaChangeSuggestionModal isOpen={!!currentRefinement} />
        : null}
    </DnaRefinementContext.Provider>
  );
};

export function useDnaRefinementContext(): DnaRefinementContextProps {
  const context = useContext(DnaRefinementContext);

  if (typeof context === "undefined") {
    throw new Error(
      "useDnaRefinementContext should be used within the DnaRefinementProvider!",
    );
  }

  return context;
}